import React, { useEffect, useState } from 'react';
import { Col, Image, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import BulletinpaieService from '../../services/BulletinpaieService';
import PersonnelService from '../../services/PersonnelService';
import { format, parse } from 'date-fns';
import frLocale from 'date-fns/locale/fr';
import Logo from 'assets/images/brand/logo/logo-icon.svg';
import ContratService from '../../services/ContratService';

const PrintInvoiceDetailsBulletinpaie = React.forwardRef((props, ref) => {
	const { id } = useParams();
	const [bulletinpaie, setBulletinpaie] = useState(null);
	const [personnel, setPersonnel] = useState(null);
	const [selectedPoste, setSelectedPoste] = useState('');
	const [contrat, setContrat] = useState({});

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const POSTE_CHOICES = [
		{ value: 'enseignant', label: 'Enseignant(e)' },
		{ value: 'enseignant_principal', label: 'Enseignant Principal' },
		{ value: 'surveillant_general', label: 'Surveillant Général' },
		{ value: 'conseiller_pedagogique', label: 'Conseiller Pédagogique' },
		{ value: 'infirmier', label: 'Infirmier' },
		{ value: 'bibliothecaire', label: 'Bibliothécaire' },
		{ value: 'technicien_informatique', label: 'Technicien Informatique' },
		{ value: 'agent_de_service', label: 'Agent de Service' },
		{ value: 'gardien', label: 'Gardien' },
		{ value: 'chauffeur', label: 'Chauffeur' },
		{ value: 'cantine', label: 'Responsable de la Cantine' },
		{ value: 'psychologue_scolaire', label: 'Psychologue Scolaire' },
		{ value: 'educateur_specialise', label: 'Éducateur Spécialisé' },
		{ value: 'assistant_pedagogique', label: 'Assistant Pédagogique' },
		{ value: 'assistant_administratif', label: 'Assistant Administratif' }
	];

	useEffect(() => {
		BulletinpaieService.getBulletinpaie(id)
			.then((bulletinpaieData) => {
				setBulletinpaie(bulletinpaieData.data);
			})
			.catch((error) => {
				console.error('Error fetching bulletinpaie data', error);
			});
	}, [id]);

	useEffect(() => {
		// Vérifier si le bulletin de paie a été récupéré et n'est pas nul
		if (bulletinpaie) {
			Promise.all([PersonnelService.getPersonnel(bulletinpaie.personnel)])
				.then(([personnelData]) => {
					setPersonnel(personnelData.data);

					if (personnelData && personnelData.data) {
						const personnelPoste = personnelData.data.poste;
						const posteOption = POSTE_CHOICES.find(
							(option) => option.value === personnelPoste
						);
						setSelectedPoste(posteOption ? posteOption : '');
					}
				})
				.catch((error) => {
					console.error('Error fetching personnel data', error);
				});
		}
	}, [POSTE_CHOICES, bulletinpaie]);

	useEffect(() => {
		// Vérifier si le bulletin de paie a été récupéré et n'est pas nul
		if (bulletinpaie) {
			Promise.all([ContratService.getContrat(bulletinpaie.contrat)])
				.then(([contratData]) => {
					setContrat(contratData.data);
				})

				.catch((error) => {
					console.error('Error fetching contrat data', error);
				});
		}
	}, [bulletinpaie]);

	const formatPeriodePaieBulletinpaie = (value) => {
		const date = parse(value, 'yyyy-MM', new Date());
		const formattedDate = format(date, 'MMMM yyyy', { locale: frLocale });
		return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
	};

	// Function to format number to locale string with FCFA currency
	const formatCurrency = (value) => {
		return new Intl.NumberFormat('fr-FR', {
			style: 'currency',
			currency: 'XAF'
		}).format(value);
	};

	const calculateOvertimeSalary = (
		heures_supplementaires,
		taux_heures_supplementaires
	) => {
		return heures_supplementaires * taux_heures_supplementaires;
	};

	const calculateTotalRevenues = (
		heures_supplementaires,
		taux_heures_supplementaires,
		salaire_brut,
		indemnites
	) => {
		const overtimeSalary = calculateOvertimeSalary(
			heures_supplementaires,
			taux_heures_supplementaires
		);

		const totalRevenues =
			Number(salaire_brut) + Number(overtimeSalary) + Number(indemnites);
		return formatCurrency(totalRevenues);
	};

	const calculateTotalRetenues = (
		cotisation_cnps,
		deduction_cnps,
		retenues
	) => {
		const totalRetenues =
			Number(cotisation_cnps) + Number(deduction_cnps) + Number(retenues);
		return formatCurrency(totalRetenues);
	};

	const scale = 0.9;

	return (
		<div
			ref={ref}
			style={{
				transform: `scale(${scale})`,
				minHeight: '1500px',
				// minHeight: '841.92px',
				transformOrigin: 'top',
				boxSizing: 'border-box'
			}}
			className="d-flex flex-column text-black justify-content-between w-100"
		>
			{bulletinpaie && personnel && (
				<>
					<Row className="small mb-3">
						<Col>
							<Image src={Logo} alt="" className="mb-4" width="80%" />
							<h4 className="mb-2">FONDATION FOZEU ET MAGAKOU</h4>
							<small className="mb-4">
								BULLETIN DE PAIE: #{bulletinpaie.id}
							</small>
						</Col>
					</Row>

					<Row className="small mb-4">
						<Col>
							<h5 className="mb-3">Détails de l'employé</h5>
							<strong>Nom: </strong>
							<span className="text-uppercase">
								{personnel.nom} {personnel.prenom}
							</span>
							<br />
							<strong>Adresse: </strong>
							<span className="text-uppercase">{personnel.adresse}</span>
							<br />
							<strong>Téléphone: </strong>
							<span className="text-uppercase">{personnel.telephone}</span>
							<br />
							<strong>Email: </strong> {personnel.email}
							<br />
							<strong>Poste: </strong> {selectedPoste.label}
						</Col>
						<Col>
							<h5 className="mb-3">Détails du Bulletin de Paie</h5>
							<strong>Période:</strong>{' '}
							{formatPeriodePaieBulletinpaie(bulletinpaie.periode_paie)}
							<br />
							<strong>Date de Paiement:</strong>{' '}
							{new Date(bulletinpaie.date_paiement).toLocaleDateString()}
							<br />
							<strong>Taux Horaire:</strong>{' '}
							{formatCurrency(bulletinpaie.taux_heures_supplementaires)}
							<br />
							<strong>Heures Supplémentaires:</strong>
							<br /> {bulletinpaie.heures_supplementaires} heure(s)
						</Col>
					</Row>

					<Row className="mb-auto small">
						<table className="table table-light table-sm table-bordered text-nowrap">
							<thead>
								<tr>
									<th scope="col" width="40%">
										Revenus
									</th>
									<th scope="col">Montant (FCFA)</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Salaire de Base</td>
									<td>{formatCurrency(contrat.salaire_brut)}</td>
								</tr>
								<tr>
									<td>Salaire des Heures Supplémentaires</td>
									<td>
										{formatCurrency(
											calculateOvertimeSalary(
												bulletinpaie.heures_supplementaires,
												bulletinpaie.taux_heures_supplementaires
											)
										)}
									</td>
								</tr>
								<tr>
									<td>Indemnités</td>
									<td>{formatCurrency(bulletinpaie.indemnites)}</td>
								</tr>
							</tbody>
							<tfoot>
								<tr className="fw-bold">
									<td>Total des Revenus</td>
									<td>
										{calculateTotalRevenues(
											bulletinpaie.heures_supplementaires,
											bulletinpaie.taux_heures_supplementaires,
											contrat.salaire_brut,
											bulletinpaie.indemnites
										)}
									</td>
								</tr>
							</tfoot>
						</table>

						<table className="table table-light table-sm table-bordered text-nowrap">
							<thead>
								<tr>
									<th scope="col" width="40%">
										Retenues
									</th>
									<th scope="col">Montant (FCFA)</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Déductions CNPS</td>
									<td>{formatCurrency(personnel.deduction_cnps)}</td>
								</tr>
								<tr>
									<td>Cotisations CNPS</td>
									<td>{formatCurrency(personnel.cotisation_cnps)}</td>
								</tr>
								<tr>
									<td>Autres Retenues</td>
									<td>{formatCurrency(bulletinpaie.retenues)}</td>
								</tr>
								{/* Add other relevant deductions here */}
							</tbody>
							<tfoot>
								<tr className="fw-bold">
									<td>Total des Retenues</td>
									<td>
										{calculateTotalRetenues(
											personnel.cotisation_cnps,
											personnel.deduction_cnps,
											bulletinpaie.retenues
										)}
									</td>
								</tr>
							</tfoot>
						</table>
					</Row>

					<Row className="small">
						<Row className="mb-4">
							<Col>
								<h5 className="pt-5">Salaire Net:</h5>
								<h2 className="fw-bolder">
									{formatCurrency(bulletinpaie.salaire_net)}
								</h2>
							</Col>
							<Col>
								<div className="text-muted">
									<p className="my-5">Signature: ________________________</p>
									<p className="my-5">Cachet: ________________________</p>
								</div>
							</Col>
						</Row>
						<Col>
							<p className="border-top pt-3 fs-6">
								Note: Ce bulletin de paie est émis à titre informatif. Toutes
								les informations contenues dans ce document sont confidentielles
								et réservées uniquement à l'usage du personnel et de
								l'administration de la Fondation Fozeu et Magakou. En cas de
								divergence ou d'erreur, veuillez en informer immédiatement le
								service des ressources humaines pour rectification. Merci.
							</p>
						</Col>
					</Row>
				</>
			)}
		</div>
	);
});

export default PrintInvoiceDetailsBulletinpaie;
